import { getRouteApi } from '@tanstack/react-router';
import {
    DATA_TABLE_SIZE,
    DataTableColumn,
    DataTableContextProvider,
    DataTableRow,
} from 'cpool-components/general/DataTable';
import { EMPTY_PLACEHOLDER } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { GoInfo } from 'react-icons/go';

import { Commission } from '../../Overview/OfferManagement/Table/Cells/Commission';
import { BankLogoWithDescription } from '~/components/BankLogoWithDescription';
import { RSV } from '~/components/RSV/RSV';
import type { OfferEntry } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory/schemas/offers.schema';
import { HouseHoldCalculations } from '~/features/leadDetails/components/Overview/OfferManagement/Table/Cells/Actions/HouseHoldCalculations.tsx';
import { Info } from '~/features/leadDetails/components/Overview/OfferManagement/Table/Cells/Actions/info.tsx';
import { currencyFormat, numberFormat } from '~/lib/i18n';
import { cn } from '~/utils/cnTailwind';

const COLUMNS = [
    'BANK',
    'PROVIDER_APPLICATION_ID',
    'CREDIT_AMOUNT',
    'RUNTIME',
    'RATE',
    'INTEREST',
    'RESIDUAL_DEBT_INSURANCE',
    'PROVISION',
    'STATUS',
] as const;

const OfferRow = ({ leadId, payload: offer }: { payload: OfferEntry; leadId: string }) => {
    const { t } = useTranslation();

    return (
        <DataTableRow payload={offer} className="text-center text-sm text-midnight [&>td]:px-2 [&>td]:py-2.5">
            <td>
                <BankLogoWithDescription
                    bankName={offer.bank}
                    description={offer.produktbezeichnung}
                    bankLogo={offer.bankSvg}
                    imgClassName={cn(
                        'w-[85px] min-w-[85px] max-h-[25px]',
                        offer.bank && offer.bank.includes('consorsFinanz') ? 'w-5 min-w-5' : '',
                    )}
                />
            </td>
            <td>
                {offer.acceptedOffer?.providerApplicationNumber
                    ? offer.acceptedOffer.providerApplicationNumber
                    : EMPTY_PLACEHOLDER}
            </td>
            <td>{currencyFormat(offer.requestAmount)}</td>
            <td>{t('BANK_CREDIT_REQUEST.HISTORY.TABLE.LOAN_RUNTIME', { count: offer.loanRuntime })}</td>
            <td>{currencyFormat(offer.monthlyRate)}</td>
            <td>
                {t('BANK_CREDIT_REQUEST.HISTORY.TABLE.EFFECTIVE_INTEREST_RATE', {
                    rate: numberFormat(offer.effectiveInterestRate),
                })}
            </td>
            <RSV
                applicant1ResidualDebtInsurance={
                    offer.residualDebtInsurance.applicant1.length ? offer.residualDebtInsurance.applicant1 : null
                }
                applicant2ResidualDebtInsurance={
                    offer.residualDebtInsurance.applicant2.length ? offer.residualDebtInsurance.applicant2 : null
                }
            />
            {offer.acceptedOffer && offer.acceptedOffer.applicationId ? (
                <Commission
                    leadId={leadId}
                    source={offer.source}
                    id={offer.id}
                    responseCommissionInPercent={offer.acceptedOffer?.commissionResponseInPercent ?? null}
                    receivedCommission={offer.acceptedOffer?.provision ?? null}
                    kreditbetrag={offer.requestAmount}
                />
            ) : (
                <td>{EMPTY_PLACEHOLDER}</td>
            )}
            <td>{offer.status && t(`OFFER_MANAGEMENT.STATUS.${offer.status}`)}</td>
            <td>
                <div className="flex items-center justify-center gap-2 text-blue">
                    {offer.acceptedOffer && offer.acceptedOffer.applicationId ? (
                        <Info offer={offer.acceptedOffer} />
                    ) : (
                        <div className="flex cursor-not-allowed items-center justify-center p-1.5 opacity-60">
                            <GoInfo size={16} />
                        </div>
                    )}
                    <HouseHoldCalculations offerId={offer.id} offerSource={offer.source} aggregateId={leadId} />
                </div>
            </td>
        </DataTableRow>
    );
};

type Props = {
    offers: Array<OfferEntry>;
};

export const HistoryTable: React.FC<Props> = ({ offers }) => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();

    return (
        <DataTableContextProvider size={DATA_TABLE_SIZE.SM}>
            <div className="overflow-x-auto rounded-md border border-grey-outline-light bg-white">
                <table className="w-full table-auto">
                    <thead>
                        <tr>
                            {COLUMNS.map((column) => (
                                <DataTableColumn
                                    key={column}
                                    field={column}
                                    align="center"
                                    name={t(`BANK_CREDIT_REQUEST.HISTORY.TABLE_HEADERS.${column}`)}
                                    className="whitespace-nowrap"
                                />
                            ))}
                            <DataTableColumn field="actions" name="" />
                        </tr>
                    </thead>
                    <tbody>
                        {offers.map((offer) => (
                            <OfferRow key={offer.id} payload={offer} leadId={leadId} />
                        ))}
                    </tbody>
                </table>
            </div>
        </DataTableContextProvider>
    );
};
